import { ArrowDown } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * GoalFlowStory — a vertical "narrative flow" of tiers, each holding one or
 * more colored boxes, with a down-arrow connecting tier to tier. Built to
 * render the 2026 "Make It Easy" story (access + use → sell), but fully
 * data-driven so each year can re-arrange the tiers/boxes without code changes.
 *
 * Boxes are clickable when `onSelect` is provided — that's the primary way into
 * a goal's detail on the Goals page. When omitted, the story is display-only.
 *
 * Design-system notes:
 * - `accent` is a DS color-token name (never a hex). The accent→class maps
 *   below are LITERAL strings on purpose: Tailwind's JIT only emits classes it
 *   can see in source, so these must never be built via template interpolation.
 * - The left accent bar + tinted fill mirror the slide treatment; the tint uses
 *   the pre-computed opaque `-light` tokens (see badge.tsx note) so the boxes
 *   read cleanly on any surface.
 */

export type GoalFlowAccent =
  | "brand"
  | "power"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "neutral"
  | "purple"
  | "teal"
  | "orange";

export interface GoalFlowBox {
  /** Stable id passed back to `onSelect` (the goal id). */
  id: string;
  title: string;
  subtitle?: string | null;
  accent: GoalFlowAccent;
}

export interface GoalFlowTier {
  /** The connective phrase shown alongside the tier (e.g. "& easier to use"). */
  label: string;
  boxes: GoalFlowBox[];
}

export interface GoalFlowStoryProps {
  tiers: GoalFlowTier[];
  /** When provided, boxes render as buttons and clicking one calls this. */
  onSelect?: (boxId: string) => void;
  selectedId?: string | null;
  className?: string;
}

const ACCENT_BAR: Record<GoalFlowAccent, string> = {
  brand: "bg-brand",
  power: "bg-power",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
  neutral: "bg-neutral",
  purple: "bg-purple",
  teal: "bg-teal",
  orange: "bg-orange",
};

const ACCENT_TINT: Record<GoalFlowAccent, string> = {
  brand: "bg-brand-light",
  power: "bg-power-light",
  success: "bg-success-light",
  warning: "bg-warning-light",
  danger: "bg-danger-light",
  info: "bg-info-light",
  neutral: "bg-neutral-light",
  purple: "bg-purple-light",
  teal: "bg-teal-light",
  orange: "bg-orange-light",
};

const ACCENT_TEXT: Record<GoalFlowAccent, string> = {
  brand: "text-brand",
  power: "text-power",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
  neutral: "text-foreground",
  purple: "text-purple",
  teal: "text-teal",
  orange: "text-orange",
};

const ACCENT_RING: Record<GoalFlowAccent, string> = {
  brand: "ring-brand",
  power: "ring-power",
  success: "ring-success",
  warning: "ring-warning",
  danger: "ring-danger",
  info: "ring-info",
  neutral: "ring-neutral",
  purple: "ring-purple",
  teal: "ring-teal",
  orange: "ring-orange",
};

function FlowBox({
  box,
  selected,
  onSelect,
}: {
  box: GoalFlowBox;
  selected: boolean;
  onSelect?: (id: string) => void;
}) {
  const inner = (
    <>
      <span className={cn("w-1.5 flex-shrink-0", ACCENT_BAR[box.accent])} aria-hidden="true" />
      <span className="flex flex-col px-4 py-3">
        <span className={cn("text-sm font-bold leading-tight", ACCENT_TEXT[box.accent])}>
          {box.title}
        </span>
        {box.subtitle ? (
          <span className="mt-0.5 text-xs text-foreground/70">{box.subtitle}</span>
        ) : null}
      </span>
    </>
  );

  const base = cn(
    "relative flex min-w-[14rem] flex-1 items-stretch overflow-hidden rounded-lg border border-transparent text-left",
    ACCENT_TINT[box.accent],
  );

  if (!onSelect) {
    return <div className={base}>{inner}</div>;
  }
  return (
    <button
      type="button"
      onClick={() => onSelect(box.id)}
      aria-pressed={selected}
      className={cn(
        base,
        "transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        ACCENT_RING[box.accent],
        selected && "shadow-md ring-2 ring-offset-2",
      )}
    >
      {inner}
    </button>
  );
}

export function GoalFlowStory({ tiers, onSelect, selectedId, className }: GoalFlowStoryProps) {
  return (
    <div className={cn("flex flex-col", className)}>
      {tiers.map((tier, i) => (
        <div key={tier.label}>
          <div className="flex flex-col gap-3 md:flex-row md:items-stretch">
            <div className="md:w-44 md:flex-shrink-0 md:pt-2.5">
              <p className="text-sm font-semibold leading-snug text-muted-foreground">
                {tier.label}
              </p>
            </div>
            <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap">
              {tier.boxes.map((box) => (
                <FlowBox
                  key={box.id}
                  box={box}
                  selected={box.id === selectedId}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </div>
          {i < tiers.length - 1 ? (
            <div className="flex justify-center py-2 md:pl-44" aria-hidden="true">
              <ArrowDown className="h-5 w-5 text-muted-foreground/60" />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
